import { Services } from '../../services';
import {
	type PanelDragFrame,
	type PanelResizeAxis,
	type PanelResizeTarget,
	panelSizeFromDrag,
	type PanelSizeConstraints,
} from './resize.geometry';
import { CSS } from './resizable.style';

const NUDGE_STEP = 16;

export interface PanelResizeOptions extends PanelSizeConstraints {
	axis: PanelResizeAxis;
	/** The panel must be `box-sizing: border-box` — sizes are measured border-box but written to a basis. */
	cssVar: string;
	target: PanelResizeTarget;
	storageKey: string;
	onResizeEnd?: () => void;
}

/**
 * The size goes to a custom property on the document root, not an observable or a vnode `style`: Mithril 1.x
 * rewrites vnode style properties on every redraw, clobbering an in-flight drag, and rendering runs in a MobX
 * autorun, so an observable written per mousemove re-renders the whole page tree every frame.
 *
 * Nothing caches the divider element. Mithril diffs unkeyed children positionally by tag, so navigating away
 * patches this `div` into the next page's `div` at the same index instead of removing it — `onremove` never
 * fires, and on the way back neither does `oncreate`.
 */
export class PanelResizeController {
	private startPointer: number = 0;
	private frame: PanelDragFrame | null = null;
	private dragged: boolean = false;
	private lastApplied: number | null = null;
	private restored: boolean = false;

	private readonly boundMove = (event: MouseEvent) => this.onMove(event);
	private readonly boundUp = () => this.onUp();

	constructor(private readonly options: PanelResizeOptions) {}

	/** One-shot per controller, and a controller is built per page visit, so it reapplies on every visit. */
	ensureRestored() {
		if (this.restored) {
			return;
		}
		this.restored = true;

		const stored = Services.LocalStorage.getItem<number>(this.options.storageKey);
		if (typeof stored !== 'number' || !Number.isFinite(stored)) {
			return;
		}
		// Only the panel's own floor: the neighbor's needs both live sizes, which are not laid out yet.
		this.apply(Math.max(stored, this.options.min));
	}

	/**
	 * Reads the live property instead of a local flag: a page revisit builds a fresh controller, which would
	 * otherwise not know the departing one had left a size on the document.
	 */
	reset() {
		this.restored = false;
		this.lastApplied = null;

		const root = Services.Window.document.documentElement;
		if (root.style.getPropertyValue(this.options.cssVar) !== '') {
			root.style.removeProperty(this.options.cssVar);
		}
	}

	dispose() {
		this.stopTracking();
	}

	onMouseDown(event: MouseEvent) {
		const handle = eventHandle(event);
		if (handle == null) {
			return;
		}

		const target = this.siblingElement(handle, 'target');
		const adjacent = this.siblingElement(handle, 'adjacent');
		if (target == null || adjacent == null) {
			return;
		}

		// preventDefault suppresses the focus the press would otherwise give the divider, breaking arrow keys.
		event.preventDefault();
		handle.focus();

		this.startPointer = this.pointerPosition(event);
		this.frame = { targetSize: this.elementSize(target), adjacentSize: this.elementSize(adjacent) };
		this.dragged = false;

		Services.Window.document.body.classList.add(this.bodyDraggingClass());
		Services.Window.window.addEventListener('mousemove', this.boundMove);
		Services.Window.window.addEventListener('mouseup', this.boundUp);
	}

	onKeyDown(event: KeyboardEvent) {
		const [backKey, forwardKey] =
			this.options.axis === 'x' ? (['ArrowLeft', 'ArrowRight'] as const) : (['ArrowUp', 'ArrowDown'] as const);
		if (event.key !== backKey && event.key !== forwardKey) {
			return;
		}

		const handle = eventHandle(event);
		if (handle == null) {
			return;
		}

		const target = this.siblingElement(handle, 'target');
		const adjacent = this.siblingElement(handle, 'adjacent');
		if (target == null || adjacent == null) {
			return;
		}

		event.preventDefault();

		const frame: PanelDragFrame = { targetSize: this.elementSize(target), adjacentSize: this.elementSize(adjacent) };
		const pointerDelta = event.key === forwardKey ? NUDGE_STEP : -NUDGE_STEP;
		this.apply(panelSizeFromDrag(pointerDelta, this.options.target, frame, this.options));
		this.commit();
	}

	private onMove(event: MouseEvent) {
		const frame = this.frame;
		if (frame == null) {
			return;
		}

		this.dragged = true;
		this.apply(
			panelSizeFromDrag(this.pointerPosition(event) - this.startPointer, this.options.target, frame, this.options)
		);
	}

	private onUp() {
		this.stopTracking();
		if (this.dragged) {
			this.commit();
		}
		this.frame = null;
	}

	private commit() {
		if (this.lastApplied != null) {
			Services.LocalStorage.setItem(this.options.storageKey, this.lastApplied);
		}
		if (this.options.onResizeEnd) {
			this.options.onResizeEnd();
		}
	}

	private apply(size: number) {
		this.lastApplied = size;
		Services.Window.document.documentElement.style.setProperty(this.options.cssVar, `${size}px`);
	}

	private stopTracking() {
		Services.Window.document.body.classList.remove(this.bodyDraggingClass());
		Services.Window.window.removeEventListener('mousemove', this.boundMove);
		Services.Window.window.removeEventListener('mouseup', this.boundUp);
	}

	private bodyDraggingClass(): string {
		return this.options.axis === 'x' ? CSS.bodyDraggingX : CSS.bodyDraggingY;
	}

	private pointerPosition(event: MouseEvent): number {
		return this.options.axis === 'x' ? event.clientX : event.clientY;
	}

	private elementSize(element: HTMLElement): number {
		const rect = element.getBoundingClientRect();
		return this.options.axis === 'x' ? rect.width : rect.height;
	}

	private siblingElement(handle: HTMLElement, which: 'target' | 'adjacent'): HTMLElement | null {
		const wantPrevious = (this.options.target === 'previous') === (which === 'target');
		const sibling = wantPrevious ? handle.previousElementSibling : handle.nextElementSibling;
		return sibling instanceof HTMLElement ? sibling : null;
	}
}

function eventHandle(event: Event): HTMLElement | null {
	return event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
}
